'use client'

import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'

import {
  CalendarClock, HeartHandshake, Play,
  ShoppingBag, Ticket, type LucideIcon,
} from 'lucide-react'
import { SiInstagram, SiSpotify, SiYoutube } from '@icons-pack/react-simple-icons'

import { MotionPreset } from '@/components/ui/motion-preset'

/**
 * Real blocks, scattered around the hero copy.
 *
 * The blocks are the product, so the hero shows blocks rather than a phone
 * frame containing them. Each card is drawn the way `PublicBlock` draws the
 * real thing: an accent icon tile, a title, a meta line, and a gradient pill
 * where that type has a call to action.
 *
 * Only decoration. Every card is `aria-hidden` and none is focusable: they say
 * nothing the headline beside them does not already say, and a screen reader
 * reading eight fake product cards before the actual pitch would be worse than
 * silence.
 *
 * Laid out with absolute percentages inside a container that reserves its own
 * height, so the cards sit in the hero's margins without pushing the copy
 * around. Hidden below `xl`: under that width there are no margins to sit in,
 * and `PhoneStack` is what carries the hero there instead.
 */

type ScatterCard = {
  /** Where the card sits, as a percentage of the container. */
  top: string
  side: 'left' | 'right'
  offset: string
  rotate: number
  /** Larger is nearer. Drives width, shadow and parallax speed. */
  near?: boolean
  icon: LucideIcon | typeof SiSpotify
  /** The icon tile's fill. A brand colour, or the app's own gradient. */
  tile: string
  title: string
  meta?: string
  cta?: string
  /** Fills the card body, the way an image or a player block does. */
  art?: string
}

const CARDS: ScatterCard[] = [
  {
    top: '3%', side: 'left', offset: '3%', rotate: -7, near: true,
    icon: SiSpotify, tile: 'bg-[#1DB954]',
    title: 'Late night mix', meta: '24 tracks', art: 'bg-gradient-to-br from-emerald-500/80 to-teal-700/80',
  },
  {
    top: '40%', side: 'left', offset: '1%', rotate: 5,
    icon: SiYoutube, tile: 'bg-[#FF0000]',
    title: 'Gadget reviews', meta: '304k',
  },
  {
    top: '66%', side: 'left', offset: '4%', rotate: -4, near: true,
    icon: ShoppingBag, tile: 'bg-gradient',
    title: 'Studio hoodie', meta: '₮89,000', cta: 'Buy',
  },
  {
    top: '4%', side: 'right', offset: '4%', rotate: 6,
    icon: CalendarClock, tile: 'bg-gradient',
    title: 'Book a time', meta: '60 min · ₮45,000', cta: 'Book',
  },
  {
    top: '30%', side: 'right', offset: '1%', rotate: -5, near: true,
    icon: Ticket, tile: 'bg-gradient',
    title: 'Live at UB', meta: 'Fri 20:00', cta: 'Get ticket',
  },
  {
    top: '80%', side: 'right', offset: '5%', rotate: 4,
    icon: HeartHandshake, tile: 'bg-gradient',
    title: 'Support my art', cta: 'Support',
  },
  {
    top: '36%', side: 'left', offset: '10%', rotate: 3,
    icon: SiInstagram, tile: 'bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]',
    title: 'Instagram', meta: '@anu',
  },
  {
    top: '54%', side: 'right', offset: '10%', rotate: -6,
    icon: Play, tile: 'bg-gradient',
    title: 'New video', art: 'bg-gradient-to-br from-fuchsia-600/70 to-purple-800/70',
  },
]

/**
 * How far a card drifts over the hero's own scroll, in pixels.
 *
 * Near cards move most and far cards least, which is the whole illusion: the
 * eye reads differing speeds as differing distance. The copy in the middle does
 * not move at all, so the blocks appear to slide past it.
 *
 * Kept small. A large offset turns a hero into a carnival ride and, worse,
 * drags cards over the headline on the way past.
 */
const DRIFT_NEAR = -90
const DRIFT_FAR = -40

const BlockScatter = () => {
  const hostRef = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()

  // Progress across the hero's own passage through the viewport, so the drift
  // is tied to this section rather than to the whole document height.
  const { scrollYProgress } = useScroll({
    target: hostRef,
    offset: ['start start', 'end start'],
  })

  const near = useTransform(scrollYProgress, [0, 1], [0, DRIFT_NEAR])
  const far = useTransform(scrollYProgress, [0, 1], [0, DRIFT_FAR])

  return (
    <div
      ref={hostRef}
      // No `overflow-hidden`: every card is positioned fully inside the
      // container, rotation included, so nothing needs clipping and nothing can
      // widen the document. Clipping here sliced the corners off the outermost
      // cards, which is what made them look cut in half.
      className='pointer-events-none absolute inset-0 hidden xl:block'
      aria-hidden='true'
    >
      {CARDS.map((card, index) => (
        // Two layers on purpose. The outer one owns the parallax `y`, which is
        // a live scroll value; the inner one owns the entrance animation, which
        // runs once. Putting both on one element would have the entrance's
        // transform overwrite the drift the moment it settled.
        <motion.div
          key={index}
          className='absolute'
          style={{
            top: card.top,
            [card.side]: card.offset,
            width: card.near ? '13.5rem' : '11.5rem',
            // Reduced motion keeps the scatter, drops the drift: the cards are
            // decoration and their arrangement is the point, not their travel.
            y: reduced ? 0 : (card.near ? near : far),
          }}
        >
        <MotionPreset
          fade
          zoom={{ initialScale: 0.9 }}
          delay={0.15 + index * 0.07}
          transition={{ duration: 0.6 }}
        >
          <div
            className={`rounded-2xl border border-border bg-card p-3.5 ${
              card.near ? 'shadow-2xl' : 'opacity-80 shadow-xl'
            }`}
            style={{ transform: `rotate(${card.rotate}deg)` }}
          >
            <span className={`flex size-9 items-center justify-center rounded-xl text-white ${card.tile}`}>
              <card.icon className='size-4.5' />
            </span>

            {/* The body an image or player block fills. Only some cards have
                one, the way only some block types do. */}
            {card.art && <div className={`mt-3 h-16 rounded-xl ${card.art}`} />}

            <p className='mt-3 truncate font-gilroy text-[13px] font-bold'>{card.title}</p>
            {card.meta && (
              <p className='mt-0.5 truncate text-[11px] font-medium text-muted-foreground'>{card.meta}</p>
            )}
            {card.cta && (
              <span className='bg-gradient mt-2.5 flex items-center justify-center rounded-full px-3 py-1.5 font-gilroy text-[11px] font-semibold text-white'>
                {card.cta}
              </span>
            )}
          </div>
        </MotionPreset>
        </motion.div>
      ))}
    </div>
  )
}

export default BlockScatter
